import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useProfile, useQualifications, useWorkExperience, useDocuments } from '../../hooks';
import { Card, CardHeader, CardBody, Button, Avatar, Spinner } from '../../components/ui';
import { PassportStatusBadge } from '../../components/PassportStatus';
import { VerificationBadge } from '../../components';
import {
  getFieldOfWorkLabel,
  isAtLeastConfirmed,
  recordStatus,
} from '../../utils';

const StatCard: React.FC<{ value: number; label: string; color: string; bg: string; icon: string }> = ({
  value, label, color, bg, icon
}) => (
  <Card>
    <CardBody>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${bg}`}>
          <svg className={`w-5 h-5 ${color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
          </svg>
        </div>
        <div className="min-w-0">
          <p className={`text-2xl font-extrabold leading-none ${color}`}>{value}</p>
          <p className="text-xs text-gray-500 mt-0.5 font-medium">{label}</p>
        </div>
      </div>
    </CardBody>
  </Card>
);

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const { qualifications, loading: qualificationsLoading } = useQualifications();
  const { workExperience, loading: experienceLoading } = useWorkExperience();
  const { documents } = useDocuments();

  const loading = profileLoading || qualificationsLoading || experienceLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const verifiedQualifications = qualifications.filter(q =>
    isAtLeastConfirmed(recordStatus(q)),
  ).length;
  const firstName = profile?.first_name || user?.email?.split('@')[0] || 'there';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-extrabold text-gray-900 break-words">
            Welcome back, {firstName} 👋
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{"Here's an overview of your Passport"}</p>
        </div>
        <PassportStatusBadge
          profile={profile}
          qualifications={qualifications}
          verifications={[]}
          workExperience={workExperience}
          documents={documents}
          size="md"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          value={qualifications.length}
          label="Qualifications"
          color="text-brand-600"
          bg="bg-brand-50"
          icon="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
        />
        <StatCard
          value={verifiedQualifications}
          label="Verified"
          color="text-emerald-600"
          bg="bg-emerald-50"
          icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
        <StatCard
          value={workExperience.length}
          label="Jobs"
          color="text-blue-600"
          bg="bg-blue-50"
          icon="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
        <StatCard
          value={documents.length}
          label="Documents"
          color="text-amber-600"
          bg="bg-amber-50"
          icon="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </div>

      {/* Profile + Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Profile */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <h2 className="text-sm font-bold text-gray-900">My Passport</h2>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex items-center gap-3 mb-4 min-w-0">
              <Avatar
                src={profile?.photo_url}
                alt="Profile"
                size="lg"
                fallback={profile?.first_name?.charAt(0) || profile?.last_name?.charAt(0)}
                className="ring-2 ring-brand-100 shrink-0"
              />
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 text-sm break-words">
                  {[profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || 'Complete your profile'}
                </p>
                <p className="text-xs text-brand-600 mt-0.5 font-medium break-words">
                  {profile?.field_of_work ? getFieldOfWorkLabel(profile.field_of_work) : 'Add your field of work'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/profile" className="flex-1">
                <Button variant="primary" fullWidth size="sm">View</Button>
              </Link>
              <Link to="/profile/edit" className="flex-1">
                <Button variant="secondary" fullWidth size="sm">Edit</Button>
              </Link>
            </div>
          </CardBody>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h2 className="text-sm font-bold text-gray-900">Quick Add</h2>
            </div>
          </CardHeader>
          <CardBody>
            <div className="space-y-2">
              {[
                { to: "/qualifications/add", label: "Qualification", icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" },
                { to: "/experience/add", label: "Work Experience", icon: "M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" },
                { to: "/documents/upload", label: "Document", icon: "M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" },
              ].map(({ to, label, icon }) => (
                <Link key={to} to={to} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-gray-100 hover:border-brand-200 hover:bg-brand-50 transition-colors group">
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-brand-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                  </svg>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-brand-700 transition-colors flex-1">{label}</span>
                  <svg className="w-3.5 h-3.5 text-gray-300 group-hover:text-brand-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </Link>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Export CV */}
        <Card className="md:col-span-2 lg:col-span-1">
          <CardHeader>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <h2 className="text-sm font-bold text-gray-900">Export CV</h2>
            </div>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-gray-500 mb-4 leading-relaxed">
              Generate a professional CV from your Passport profile, ready to download as PDF.
            </p>
            <Link to="/profile/cv" className="block">
              <Button variant="primary" fullWidth size="sm">
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Open CV Builder
              </Button>
            </Link>
          </CardBody>
        </Card>
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <div className="flex items-start gap-2 min-w-0">
              <svg className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-bold text-gray-900 leading-snug">Qualifications</h2>
                <Link to="/qualifications" className="inline-block mt-1 text-xs font-semibold text-brand-600 hover:text-brand-700">
                  View all
                </Link>
              </div>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {qualifications.length > 0 ? (
              <div>
                {qualifications.slice(0, 3).map((qual, i) => {
                  return (
                  <div key={qual.id} className={`px-5 py-3 ${i < Math.min(qualifications.length, 3) - 1 ? 'border-b border-gray-50' : ''}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-gray-900 break-words">{qual.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{qual.institution}</p>
                      <div className="mt-1.5">
                        <VerificationBadge status={qual.verification_status} />
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="px-5 py-8 text-center">
                <p className="text-sm text-gray-400 mb-3">No qualifications yet</p>
                <Link to="/qualifications/add">
                  <Button variant="primary" size="sm">Add First</Button>
                </Link>
              </div>
            )}
          </CardBody>
        </Card>

      </div>
    </div>
  );
};
